import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import type { FormControl } from '@angular/forms';
import { ControlContainer, NgForm, ReactiveFormsModule } from '@angular/forms';

import { BehaviorSubject, distinctUntilChanged, startWith, switchMap } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import type { IconName } from '@core/user-interface-legacy';
import {
  FormErrorDirective,
  FormFieldComponent,
  InputComponent,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

import { SymbolComponent } from '../symbol/symbol.component';
import { SymbolPickerService } from '../symbol-picker.service';
import type { AnySymbol } from '../symbol.types';

@Component({
  selector: 'rt-name-symbol-input',
  imports: [
    InputComponent,
    ReactiveFormsModule,
    FormFieldComponent,
    FormErrorDirective,
    SymbolComponent,
  ],
  templateUrl: './name-symbol-input.component.html',
  styleUrls: ['./name-symbol-input.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: ControlContainer,
      useExisting: NgForm,
    },
    SymbolPickerService,
  ],
})
export class NameSymbolInputComponent implements OnChanges, OnInit {
  @Input({ required: true }) public nameControl: FormControl<string>;
  @Input({ required: true }) public symbolControl: FormControl<AnySymbol | null>;

  @Input() public fallbackSymbol?: AnySymbol;
  @Input() public fallbackIcon?: IconName;
  @Input() public forceFallback = false;

  @Input() public formErrors: Record<string, string> = {};
  @Input() public placeholder: string;
  @Input() public disabled = false;
  @Input() public autoFocus: boolean = false;
  @Input() public width: string;
  @Input() public maxLength: number;

  private symbolControl$ = new BehaviorSubject<FormControl<AnySymbol | null> | null>(null);
  private untilDestroyed = injectUntilDestroyed();

  constructor(
    private symbolPickerService: SymbolPickerService,
    protected cdRef: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.symbolControl) {
      this.symbolControl$.next(this.symbolControl);
    }
  }

  public ngOnInit() {
    this.nameControl.valueChanges
      .pipe(distinctUntilChanged(), this.untilDestroyed)
      .subscribe(v =>
        this.nameControl.setValue(v, { emitEvent: false, emitViewToModelChange: false }),
      );

    this.symbolControl$
      .pipe(
        filterBoolean,
        switchMap(symbolControl => symbolControl.valueChanges.pipe(startWith(symbolControl.value))),
        this.untilDestroyed,
      )
      .subscribe(() => this.cdRef.detectChanges());
  }

  public onSymbolClick(symbolOrigin: HTMLElement) {
    if (this.disabled) return;

    this.openEmojiPicker(symbolOrigin);
  }

  public onNameBlur() {
    this.nameControl.markAsTouched();
    this.nameControl.updateValueAndValidity();
  }

  private openEmojiPicker(origin: HTMLElement) {
    const overlayOrigin = origin.getBoundingClientRect();
    const { select$, clear$ } = this.symbolPickerService.open(overlayOrigin, {
      preferredPositions: [
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' },
      ],
      withSearchInput: true,
      dataSources: ['emojis', 'icons'],
      hasClearButton: true,
    });
    select$.subscribe(symbol => {
      this.symbolPickerService.close();
      const value =
        symbol.dataSourceId === 'emojis'
          ? <const>{ type: 'emoji', unicode: symbol.value.symbol }
          : <const>{ type: 'icon', name: symbol.value };
      this.symbolControl.markAsDirty();
      this.symbolControl.setValue(value);
    });
    clear$.subscribe(() => {
      this.symbolControl.markAsDirty();
      this.symbolControl.setValue(null);
      this.symbolPickerService.close();
    });
  }
}
